// src/contexts/ReportContext.js

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { ReportService, REPORT_TYPES } from '../services/ReportService';
import PDFGeneratorService from '../services/PDFGeneratorService';
import { formatFileDate, formatTime } from '../models/TimeEntryReportModel';

const ReportContext = createContext();

// Module-level helper: no dependency on state or props
const getDefaultReportType = (category) => {
  const firstType = REPORT_TYPES.find(type => type.category === category);
  return firstType ? firstType.id : REPORT_TYPES[0].id;
};

/**
 * Provider component for report generation functionality
 */
export const ReportProvider = ({ children, reportCategory = 'time-tracking' }) => {
  const { t, i18n } = useTranslation('reports');
  const { currentUser, currentCompany } = useAuth();
  const companyId = currentCompany?.id;

  // Date range state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1);
    date.setHours(0, 0, 0, 0);
    return date;
  });

  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  });

  // Report type state - initialized from active category
  const [reportType, setReportType] = useState(() => getDefaultReportType(reportCategory));

  // Staff selection state
  const [staffMembers, setStaffMembers] = useState([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState(new Set());
  const [isSelectingAll, setIsSelectingAll] = useState(true);

  // Report generation state
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);

  // Company and department info
  const [companyName, setCompanyName] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [departmentId, setDepartmentId] = useState(null);

  // Report caching
  const [cachedReports, setCachedReports] = useState({});
  const [lastReportParams, setLastReportParams] = useState(null);

  /**
   * Reset report type and clear data when category changes
   */
  useEffect(() => {
    const defaultType = getDefaultReportType(reportCategory);
    setReportType(defaultType);
    setReportData([]);
    setLastReportParams(null);
    setErrorMessage(null);
  }, [reportCategory]);

  /**
   * Initialize with department ID
   */
  const initializeWithDepartment = useCallback(async (deptId) => {
    if (!companyId || !deptId) return;

    setDepartmentId(deptId);

    try {
      const staffList = await ReportService.loadStaffMembers(companyId, deptId);
      setStaffMembers(staffList);

      setSelectedStaffIds(new Set());
      setIsSelectingAll(false);

      const { companyName, departmentName } = await ReportService.loadCompanyAndDepartmentInfo(
        companyId,
        deptId
      );

      setCompanyName(companyName);
      setDepartmentName(departmentName);
    } catch (error) {
      console.error('Error initializing report context:', error);
      setErrorMessage(t('failedToLoadDepartmentData'));
    }
  }, [companyId, t]);

  /**
   * Toggle selection of all staff members
   */
  const toggleSelectAll = useCallback(() => {
    if (isSelectingAll) {
      setSelectedStaffIds(new Set());
    } else {
      setSelectedStaffIds(new Set(staffMembers.map(staff => staff.id)));
    }
    setIsSelectingAll(!isSelectingAll);
  }, [isSelectingAll, staffMembers]);

  /**
   * Toggle selection of a specific staff member
   */
  const toggleStaffSelection = useCallback((staffId) => {
    setSelectedStaffIds(prevSelected => {
      const newSelected = new Set(prevSelected);

      if (newSelected.has(staffId)) {
        newSelected.delete(staffId);
      } else {
        newSelected.add(staffId);
      }

      setIsSelectingAll(newSelected.size === staffMembers.length);

      return newSelected;
    });
  }, [staffMembers]);

  /**
   * Generate report - routes to correct service method based on category
   */
  const generateReport = useCallback(async (reportTypeOverride = null) => {
    if (!companyId || !departmentId) {
      setErrorMessage(t('missingCompanyOrDepartment'));
      return;
    }

    if (selectedStaffIds.size === 0) {
      setErrorMessage(t('pleaseSelectStaffMember'));
      return;
    }

    const currentReportType = reportTypeOverride || reportType;
    const staffIdsArray = Array.from(selectedStaffIds).sort();

    // Cache key includes category
    const cacheKey = `${reportCategory}-${currentReportType}-${formatFileDate(startDate)}-${formatFileDate(endDate)}-${staffIdsArray.join(',')}`;

    const currentParams = {
      reportCategory,
      reportType: currentReportType,
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      selectedStaffIds: new Set(selectedStaffIds)
    };

    // Only serve from cache when the cached result is a non-empty array.
    // An empty array [] is truthy in JavaScript, which would permanently cache
    // zero-result queries and silently block fresh Firestore requests for the
    // same date range after the user adjusts their selection.
    if (
      Array.isArray(cachedReports[cacheKey]) &&
      cachedReports[cacheKey].length > 0 &&
      lastReportParams?.reportCategory === reportCategory &&
      lastReportParams?.reportType === currentReportType &&
      lastReportParams?.startDate.getTime() === startDate.getTime() &&
      lastReportParams?.endDate.getTime() === endDate.getTime() &&
      areSetsEqual(lastReportParams?.selectedStaffIds, selectedStaffIds)
    ) {
      setReportData(cachedReports[cacheKey]);
      return;
    }

    setIsGeneratingReport(true);
    setErrorMessage(null);

    try {
      let entries;

      if (reportCategory === 'vacation') {
        entries = await ReportService.loadVacationEntries({
          companyId,
          departmentId,
          staffIds: staffIdsArray,
          startDate,
          endDate,
          staffMembers
        });
      } else if (reportCategory === 'schedule') {
        entries = await ReportService.loadScheduleEntries({
          companyId,
          departmentId,
          staffIds: staffIdsArray,
          startDate,
          endDate,
          staffMembers
        });
      } else if (reportCategory === 'sick-leave') {
        entries = await ReportService.loadSickLeaveEntries({
          companyId,
          departmentId,
          staffIds: staffIdsArray,
          startDate,
          endDate,
          staffMembers
        });
      } else {
        // time-tracking: existing path unchanged
        const staffNames = await ReportService.getStaffNames(
          companyId,
          staffIdsArray
        );

        entries = await ReportService.loadTimeEntries({
          companyId,
          staffIds: staffIdsArray,
          startDate,
          endDate,
          reportType: currentReportType,
          staffNames
        });
      }

      setReportData(entries);
      setLastReportParams(currentParams);

      setCachedReports(prev => ({
        ...prev,
        [cacheKey]: entries
      }));
    } catch (error) {
      console.error('Error generating report:', error);
      setErrorMessage(t('failedToGenerateReport'));
    } finally {
      setIsGeneratingReport(false);
    }
  }, [
    companyId,
    departmentId,
    reportCategory,
    reportType,
    startDate,
    endDate,
    selectedStaffIds,
    staffMembers,
    cachedReports,
    lastReportParams,
    t
  ]);

  /**
   * Handle report type change with auto-regeneration
   */
  const handleReportTypeChange = useCallback((newType) => {
    setReportType(newType);

    if (reportData.length > 0 && lastReportParams) {
      const staffIdsArray = Array.from(selectedStaffIds).sort();

      // Cache key includes category
      const cacheKey = `${reportCategory}-${newType}-${formatFileDate(startDate)}-${formatFileDate(endDate)}-${staffIdsArray.join(',')}`;

      if (cachedReports[cacheKey]) {
        setReportData(cachedReports[cacheKey]);
        setLastReportParams({
          ...lastReportParams,
          reportType: newType
        });
      } else {
        setTimeout(() => {
          generateReport(newType);
        }, 0);
      }
    }
  }, [
    reportData,
    lastReportParams,
    reportCategory,
    selectedStaffIds,
    startDate,
    endDate,
    cachedReports,
    generateReport
  ]);

  /**
   * Export report as CSV
   */
  const exportCSV = useCallback(() => {
    if (reportData.length === 0) {
      setErrorMessage(t('noReportDataToExport'));
      return;
    }

    try {
      const csvContent = ReportService.generateCSV(reportData, reportType, t);

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      const fileName = `Klokify_${reportType.replace(/\s+/g, '_')}_${formatFileDate(new Date())}.csv`;

      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setErrorMessage(t('failedToExportCSV'));
    }
  }, [reportData, reportType, t]);

  /**
   * Export report as PDF - passes reportCategory to service
   */
  const exportPDF = useCallback(async () => {
    if (reportData.length === 0) {
      setErrorMessage(t('noReportDataToExport'));
      return;
    }

    try {
      const fileName = `Klokify_${reportType.replace(/\s+/g, '_')}_${formatFileDate(new Date())}.pdf`;

      await PDFGeneratorService.downloadPDF({
        title: `${companyName} - ${reportType}`,
        reportType,
        reportCategory,
        startDate,
        endDate,
        data: reportData,
        companyName,
        departmentName,
        t,
        locale: i18n.language
      }, fileName);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setErrorMessage(t('failedToExportPDF'));
    }
  }, [
    reportData,
    reportType,
    reportCategory,
    companyName,
    departmentName,
    startDate,
    endDate,
    t,
    i18n.language
  ]);

  /**
   * Share report data - handles all four categories.
   * Sick leave reports share date and staff name only - no duration values.
   * Vacation reports share date and staff name only - no duration values.
   */
  const shareReport = useCallback(() => {
    if (reportData.length === 0) {
      setErrorMessage(t('noReportDataToShare'));
      return;
    }

    try {
      let summaryText = `${t('reportTitle', { reportType })}\n`;
      summaryText += `${t('dateRange')}: ${formatDate(startDate)} - ${formatDate(endDate)}\n\n`;

      if (reportCategory === 'vacation') {
        // Each entry represents one vacation day - show date and staff name only
        reportData.forEach(entry => {
          const formattedDate = entry.getFormattedDate
            ? entry.getFormattedDate()
            : entry.date;
          summaryText += `${entry.staffName}: ${formattedDate}\n`;
        });
        summaryText += `\n${t('total')}: ${reportData.length} ${t('vacationDays')}\n`;

      } else if (reportCategory === 'sick-leave') {
        // Each entry represents one sick day - show date and staff name only.
        // durationMinutes is intentionally excluded to match the days-only reporting model.
        reportData.forEach(entry => {
          const formattedDate = entry.getFormattedDate
            ? entry.getFormattedDate()
            : entry.date;
          summaryText += `${entry.staffName}: ${formattedDate}\n`;
        });
        summaryText += `\n${t('total')}: ${reportData.length} ${t('sickDays')}\n`;

      } else if (reportCategory === 'schedule') {
        reportData.forEach(entry => {
          summaryText += `${t('staff')}: ${entry.staffName}, ${t('date')}: ${entry.date}\n`;
          summaryText += `${t('startTime')}: ${entry.startTime}, ${t('endTime')}: ${entry.endTime}\n\n`;
        });

      } else {
        // time-tracking
        switch (reportType) {
          // Braces keep this case's declarations out of the shared switch scope
          // (Daily Summary below declares totalWork / totalBreak without braces).
          case 'Working Time Record': {
            reportData.forEach(entry => {
              summaryText += `${t('staff')}: ${entry.staffName}, ${t('date')}: ${formatDate(entry.date)}\n`;
              summaryText += `${t('clockIn')}: ${formatTime(entry.clockIn)}, ${t('clockOut')}: ${formatTime(entry.clockOut)}\n`;
              summaryText += `${t('presenceDuration')}: ${formatDuration(entry.presenceDuration)}, ${t('breakTime')}: ${formatDuration(entry.breakDuration)}, ${t('workDuration')}: ${formatDuration(entry.workDuration)}\n\n`;
            });

            const recordPresence = reportData.reduce((sum, entry) => sum + entry.presenceDuration, 0);
            const recordBreak = reportData.reduce((sum, entry) => sum + entry.breakDuration, 0);
            const recordWork = reportData.reduce((sum, entry) => sum + entry.workDuration, 0);
            summaryText += `${t('total')} - ${t('presenceDuration')}: ${formatDuration(recordPresence)}, ${t('breakTime')}: ${formatDuration(recordBreak)}, ${t('workDuration')}: ${formatDuration(recordWork)}\n`;
            break;
          }

          case 'All Time Entries':
            reportData.slice(0, 50).forEach(entry => {
              summaryText += `${t('staff')}: ${entry.staffName}, ${t('date')}: ${formatDate(entry.date)}\n`;
              summaryText += `${t('session')}: ${entry.sessionId || '1'}, ${t('clockIn')}: ${formatTime(entry.clockIn)}, ${t('clockOut')}: ${formatTime(entry.clockOut)}\n`;
              summaryText += `${t('workDuration')}: ${formatDuration(entry.workDuration)}\n\n`;
            });

            if (reportData.length > 50) {
              summaryText += t('andMoreEntries', { count: reportData.length - 50 }) + '\n';
            }
            break;

          case 'Daily Summary':
          default:
            reportData.forEach(entry => {
              summaryText += `${t('staff')}: ${entry.staffName}, ${t('date')}: ${formatDate(entry.date)}\n`;
              summaryText += `${t('workHours')}: ${formatDuration(entry.workDuration)}, ${t('breakHours')}: ${formatDuration(entry.breakDuration)}\n\n`;
            });

            const totalWork = reportData.reduce((sum, entry) => sum + entry.workDuration, 0);
            const totalBreak = reportData.reduce((sum, entry) => sum + entry.breakDuration, 0);
            summaryText += `${t('total')} - ${t('workHours')}: ${formatDuration(totalWork)}, ${t('breakHours')}: ${formatDuration(totalBreak)}\n`;
            break;
        }
      }

      if (navigator.share) {
        navigator.share({
          title: t('reportTitle', { reportType }),
          text: summaryText,
        }).catch(error => {
          console.error('Error sharing report:', error);
        });
      } else {
        navigator.clipboard.writeText(summaryText).then(() => {
          alert(t('reportCopiedToClipboard'));
        }).catch(error => {
          console.error('Error copying to clipboard:', error);
          setErrorMessage(t('couldNotCopyReport'));
        });
      }
    } catch (error) {
      console.error('Error sharing report:', error);
      setErrorMessage(t('failedToShareReport'));
    }
  }, [reportData, reportType, reportCategory, startDate, endDate, t]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setErrorMessage(null);
  }, []);

  // Helper function to compare sets
  function areSetsEqual(a, b) {
    if (!a || !b) return false;
    if (a.size !== b.size) return false;
    for (const item of a) {
      if (!b.has(item)) return false;
    }
    return true;
  }

  // Formatter helpers used by shareReport.
  // Time formatting comes from TimeEntryReportModel (always 24h, any UI language).
  const formatDate = (date) => {
    return new Intl.DateTimeFormat(i18n.language === 'de' ? 'de-DE' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return i18n.language === 'de' ? `${hours} Std. ${minutes} Min.` : `${hours}h ${minutes}m`;
  };

  return (
    <ReportContext.Provider
      value={{
        // Report parameters
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        reportType,
        setReportType: handleReportTypeChange,
        reportTypes: REPORT_TYPES,
        reportCategory,

        // Staff selection
        staffMembers,
        selectedStaffIds,
        isSelectingAll,
        toggleSelectAll,
        toggleStaffSelection,

        // Report data
        reportData,
        isGeneratingReport,
        errorMessage,
        clearError,

        // Company/department info
        companyName,
        departmentName,
        departmentId,

        // Actions
        initializeWithDepartment,
        generateReport,
        exportCSV,
        exportPDF,
        shareReport
      }}
    >
      {children}
    </ReportContext.Provider>
  );
};

/**
 * Custom hook to use the report context
 */
export const useReportContext = () => {
  const context = useContext(ReportContext);

  if (!context) {
    throw new Error('useReportContext must be used within a ReportProvider');
  }

  return context;
};